import * as React from "react";
import { Link, useLocation } from "react-router";
import { ChevronDown, Menu as MenuIcon, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { IconButton } from "@/components/ui/IconButton";
import { IconTile } from "@/components/ui/IconTile";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { cn } from "@/lib/cn";
import { FLAT_NAV, PRODUCT_NAV, SOLUTIONS_NAV, type NavGroup } from "./nav";

/**
 * The marketing header's navigation. Two shapes of the same tree.
 *
 * Desktop gets a pair of panels under Product and Solutions, each entry carrying
 * a line of explanation. That line is the reason the panels exist at all - the
 * labels alone ("Area scan", "PPE check") mean nothing to a first-time visitor,
 * and a menu that only makes sense to someone who already knows the product is a
 * menu for the wrong audience.
 *
 * Phone gets a full-height sheet with both groups expanded. No accordions: there
 * are eleven destinations in total, which is a short scroll and one fewer thing
 * to tap.
 *
 * The panels open on hover **and** on click, and close on Escape, on an outside
 * press and on navigation. Hover alone strands keyboard and touch users; click
 * alone makes a mouse user work for something they expected to skim.
 */

export function MarketingNav({ signedIn }: { signedIn: boolean }) {
  const [openGroup, setOpenGroup] = React.useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const location = useLocation();

  // Any navigation closes everything. Without this, following a link inside a
  // panel leaves the panel hanging over the page it just went to.
  React.useEffect(() => {
    setOpenGroup(null);
    setSheetOpen(false);
  }, [location.pathname, location.hash]);

  // The sheet is full-height and fixed, so the page behind it must not scroll
  // under it - on iOS that is what produces the "scrolled away and lost the
  // menu" behaviour.
  React.useEffect(() => {
    if (!sheetOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [sheetOpen]);

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpenGroup(null);
      setSheetOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      {/* ── Desktop ─────────────────────────────────────────────────────── */}
      <nav aria-label="Main" className="nav__desktop" onMouseLeave={() => setOpenGroup(null)}>
        <GroupTrigger
          group={PRODUCT_NAV}
          open={openGroup === PRODUCT_NAV.label}
          onOpen={() => setOpenGroup(PRODUCT_NAV.label)}
          onToggle={() =>
            setOpenGroup((current) => (current === PRODUCT_NAV.label ? null : PRODUCT_NAV.label))
          }
        />
        <GroupTrigger
          group={SOLUTIONS_NAV}
          open={openGroup === SOLUTIONS_NAV.label}
          onOpen={() => setOpenGroup(SOLUTIONS_NAV.label)}
          onToggle={() =>
            setOpenGroup((current) =>
              current === SOLUTIONS_NAV.label ? null : SOLUTIONS_NAV.label
            )
          }
        />

        {FLAT_NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onMouseEnter={() => setOpenGroup(null)}
            className={cn("nav__link", location.pathname === item.to && "nav__link--active")}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="nav__actions">
        <ThemeToggle tone="panel" />

        {signedIn ? (
          <Link to="/app">
            <Button variant="panel" size="sm">
              Open console
            </Button>
          </Link>
        ) : (
          <>
            <Link to="/login" className="nav__cta">
              <Button variant="panel-ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link to="/signup">
              <Button variant="panel" size="sm">
                Get started
              </Button>
            </Link>
          </>
        )}

        <IconButton
          onClick={() => setSheetOpen(true)}
          label="Open menu"
          aria-expanded={sheetOpen}
          tone="panel"
          size="lg"
          className="sheet__toggle"
        >
          <MenuIcon />
        </IconButton>
      </div>

      {/* ── Phone sheet ─────────────────────────────────────────────────── */}
      {sheetOpen && (
        <div className="on-panel sheet">
          <div className="sheet__head">
            <IconButton
              onClick={() => setSheetOpen(false)}
              label="Close menu"
              tone="panel"
              size="lg"
            >
              <X />
            </IconButton>
          </div>

          <div className="sheet__body">
            {[PRODUCT_NAV, SOLUTIONS_NAV].map((group) => (
              <div key={group.label} className="sheet__group">
                <Link to={group.to} className="sheet__group-link">
                  {group.label}
                </Link>
                <ul className="sheet__group-items">
                  {group.items.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} className="sheet__group-item">
                        <item.icon />
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <ul className="sheet__flat">
              {FLAT_NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="sheet__flat-item">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <Link to="/signup" className="sheet__signup">
              <Button variant="panel" size="lg" block>
                Get started
              </Button>
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

function GroupTrigger({
  group,
  open,
  onOpen,
  onToggle,
}: {
  group: NavGroup;
  open: boolean;
  onOpen: () => void;
  onToggle: () => void;
}) {
  return (
    <div className="nav__group" onMouseEnter={onOpen}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={cn("nav__trigger", open && "nav__trigger--open")}
      >
        {group.label}
        <ChevronDown className={cn("nav__chevron", open && "nav__chevron--open")} />
      </button>

      {open && (
        <div className="nav__mega">
          {/* Elevated rather than flat, which is the exception `Card` names: this
              one genuinely floats over the page instead of sitting in it. */}
          <Card className="nav__mega-panel">
            <ul>
              {group.items.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="nav__mega-item">
                    <IconTile>
                      <item.icon />
                    </IconTile>
                    <span className="nav__mega-text">
                      <span className="nav__mega-title">{item.label}</span>
                      <span className="nav__mega-blurb">{item.blurb}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
